/* eslint-disable */
import { Checkbox, Col, Divider, Row, TableProps, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Table from '~/components/Table';
import colors from '~/constants/colors';
import { useSubmitLead } from '~/controllers/agent';
import { useGetMyLeads } from '~/controllers/sales';
import { useDeepEffect, usePath } from '~/hooks';
import { useDirection } from '~/hooks/i18nHooks';
import AgentTableActions from '~/pages/agent/components/agent/AgentTableActions';
import PageHeader, { FetchingIndicator } from '~/pages/agent/components/agent/PageHeader';
import { AgentLead } from '~/types/AgentLead';
import direction from '~/utils/direction';
import { isSaleRep, isCallCenter, isContractor } from '~/utils/getDecodedJwt';

import AgentFilters from '../../agent/components/agent/filters/AgentFilters';
import LeadEdit from '../form/LeadEdit';
import {
  applicationIdCell,
  channelCell,
  companyNameCell,
  emailCell,
  leadNumberCell,
  phoneNumberCell,
  productCell,
  resultCell,
  salesAssigneeCell,
  stageCell,
  utmMediumCell,
  utmSourceCell,
  useCampaignNameCell,
  useAdGroupNameCell,
  leadOwnerCell,
} from './cells';

const SalesLeadTable: React.FC = () => {
  const { data, isFetching, total, refetch, isRefetching } = useGetMyLeads();
  const { t } = useTranslation('sales');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'leadId',
    'applicationId',
    // 'utmSource', // Unchecked by default
    // 'utmMedium', // Unchecked by default
    // 'campaignName', // Unchecked by default
    // 'adGroupName', // Unchecked by default
    'channel',
    'companyName',
    'products',
    'phoneNumber',
    'email',
    'stage',
    'assignee',
    'leadOwner',
    'result',
  ]);
  const [openLead, setOpenLead] = useState(false);
  const [savedRecord, setSavedRecord] = useState<AgentLead | undefined | any>(undefined);
  const submitLead = useSubmitLead();
  const { pagination, changePath } = usePath();
  const customPagination = { size: 'small' as const, ...pagination, total: data?.total };
  const dir = useDirection();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<AgentLead[]>([]);

  // Get dashboard title based on role
  const getDashboardTitle = () => {
    if (isSaleRep()) {
      return t('Sales Representatives Dashboard');
    } else if (isCallCenter()) {
      return t('Call Center Dashboard');
    } else if (isContractor()) {
      return t('Contractor Dashboard');
    } else {
      return t('Sales Manager Dashboard'); // Default for sales-Manager
    }
  };

  useDeepEffect(() => {
    changePath({ page: 1, perPage: 10 });
  }, []);

  const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: AgentLead[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const handleBulkActionComplete = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    refetch();
  };

  const rowSelection: TableProps<AgentLead>['rowSelection'] = {
    selectedRowKeys,
    onChange: onSelectChange,
    type: 'checkbox',
    getCheckboxProps: (record) => ({
      disabled: record?.status === 'droped',
      name: record?.companyName,
    }),
  };

  const columns = useMemo(() => {
    const allColumns = [
      leadNumberCell(visibleColumns),
      applicationIdCell(visibleColumns),
      utmSourceCell(visibleColumns),
      utmMediumCell(visibleColumns),
      useCampaignNameCell(visibleColumns),
      useAdGroupNameCell(visibleColumns),
      channelCell(visibleColumns),
      companyNameCell(visibleColumns),
      productCell(visibleColumns),
      phoneNumberCell(visibleColumns),
      emailCell(visibleColumns),
      stageCell(visibleColumns),
      salesAssigneeCell(visibleColumns),
      // leadOwnerCell(visibleColumns),
      resultCell(visibleColumns),
      // Only show actions column for non-Contractor users
      !isContractor() && {
        title: '',
        key: 'actions',
        render: (_: any, record: any) => {
          return (
            <AgentTableActions
              actions={[
                {
                  key: 'salesLeadTableActions-edit',
                  title: t('Edit'),
                  disabled: record?.stage === 'droped',
                  action: () => {
                    setSavedRecord(() => {
                      return { ...record };
                    });
                    setOpenLead(true);
                  },
                },
              ]}
            />
          );
        },
        width: 30,
      },
    ]?.filter((item: any) => item);

    return allColumns;
  }, [JSON.stringify(visibleColumns)]);

  const onChange = (checkedValues: any[]) => {
    console.log('checked = ', checkedValues);
    setVisibleColumns(checkedValues);
  };

  const options = [
    { label: 'Lead No', value: 'leadId' },
    { label: 'Application ID', value: 'applicationId' },
    { label: 'Utm Source', value: 'utmSource' },
    { label: 'Utm Medium', value: 'utmMedium' },
    { label: 'Campaign Name', value: 'campaignName' },
    { label: 'Ad Group Name', value: 'adGroupName' },
    { label: 'Channel', value: 'channel' },
    { label: 'Company Name', value: 'companyName' },
    { label: 'Products', value: 'products' },
    { label: 'Phone Number', value: 'phoneNumber' },
    { label: 'Email', value: 'email' },
    { label: 'Stage', value: 'stage' },
    { label: 'Assignee', value: 'assignee' },
    // { label: 'Lead Owner', value: 'leadOwner' },
    { label: 'Result', value: 'result' },
  ];

  return (
    <Container>
      <PageHeader
        heading={getDashboardTitle()}
        isFetching={isFetching}
        sales
        selectedLeads={selectedRows}
        onBulkActionComplete={handleBulkActionComplete}
      >
        <AgentFilters />
        <Typography.Paragraph style={{ marginTop: 24 }}>
          Result <span style={{ fontWeight: 600 }}>: {data?.total}</span>
        </Typography.Paragraph>
      </PageHeader>
      {isFetching ? (
        <FetchingIndicator style={{ fontSize: 100, placeSelf: 'flex-end' }} />
      ) : (
        <TableContainer dir={dir}>
          {/* <Divider /> */}
          <Checkbox.Group defaultValue={visibleColumns} onChange={onChange}>
            <Row justify={'space-between'}>
              {options.map((opt) => (
                <Col key={opt.value} style={{ marginBottom: 8 }}>
                  <Checkbox value={opt.value}>{opt.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
          <Divider />
          <Table
            dataSource={data?.responses}
            // rowSelection={rowSelection}
            rowKey="leadId"
            columns={columns}
            rowClassName={(record) => classNames({ 'ant-table-row-selected': record.pinned })}
            pagination={customPagination}
            onRow={() => ({
              onClick: (event) => {
                if (event.defaultPrevented) return;
              },
            })}
          />
        </TableContainer>
      )}
      <LeadEdit
        refetch={refetch}
        open={openLead}
        initialValues={savedRecord}
        setInitialValues={setSavedRecord}
        onClose={() => {
          setSavedRecord({});
          setOpenLead(false);
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TableContainer = styled.div`

  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  .ant-table table {
    width: 100%;
    border-collapse: collapse;
  }

  .ant-table-thead > tr > th {
    padding: 0;
    border-bottom: 1px solid ${colors.borderColorBase};
    border-collapse: collapse;
  }

  .ant-table-thead > tr > th:first-child {
    ${direction.border.right(0)}
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 7px 10px;
    height: 67px;
  }

  .ant-table-row td:first-child {
    ${direction.border.left(`2px solid ${colors.borderColorBase}`)}
  }

  .ant-table-row td:last-child {
    ${direction.border.right(`2px solid ${colors.borderColorBase}`)}
  }

  .ant-table-tbody > tr.ant-table-row-selected > td {
    background: ${colors.grey[50]};
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${colors.borderColorBase};
  }

  .ant-pagination-item-active {
    background-color: ${colors.blue[200]};
    border-color: ${colors.blue[200]};
    border-radius: 3px;
  }

  .ant-pagination-item-active a,
  .ant-pagination-item:hover a,
  .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-link-icon,
  .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-link-icon {
    color: ${colors.textColor};
    font-weight: 500;
  }
}
`;

export default SalesLeadTable;
